import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFeedbackModalDisplay } from "context/FeedbackModalDisplay";
import { FeedbackModel } from "model";
import { Colors, Icon, useModal } from "swing-components";

import {
  ModalSubFeedback,
  ModalSubFeedbackExists,
  type FeedbackRating,
  type SubmitFeedbackData,
} from "~components/ModalSubFeedback";
import { Button } from "~components/Molecules";
import { submitSubFeedback, translate } from "~utils";

import { ReportIssueButtonProps } from "../AdminExperience";
import { SubQuickReviewPrompt } from "./SubQuickReviewPrompt";
import { SubRepeatReviewPrompt } from "./SubRepeatReviewPrompt";

export type SubLastFeedback = {
  rating: FeedbackRating;
  date: string;
};

type GiveFeedbackProps = ReportIssueButtonProps & {
  hasFeedback?: boolean;
  feedback?: FeedbackModel[];
  /**
   * Defines how the feedback flow is initiated:
   * - "START_WITH_RATING": The user begins the review by selecting a rating directly (e.g., rating choice buttons are shown upfront).
   * - "START_WITH_BUTTON": The user initiates the review by first clicking a button, then selecting a rating.
   */
  initiationType: "START_WITH_RATING" | "START_WITH_BUTTON";
  hideIcon?: boolean;
  subProfileImgUrl?: string;
  subIsSwingHero?: boolean;
  isFavoritedSub?: boolean;
  subLastFeedback?: SubLastFeedback;
  hasFeedbackSubmissionError?: boolean;
  setHasSubmissionError?: (hasSubmissionError: boolean) => void;
};

export function GiveFeedback(props: GiveFeedbackProps) {
  const {
    subName,
    requestId, // needed for payload creation
    initiationType,
    subId,
    school,
    hideIcon = false,
    subProfileImgUrl,
    subIsSwingHero,
    isFavoritedSub,
    subLastFeedback,
    feedback,
    hasFeedback,
    hasFeedbackSubmissionError,
    setHasSubmissionError,
  } = props;

  /********* Hooks *********/

  const { id: modalId, isDisplayed, resetModal, rating } = useFeedbackModalDisplay();

  const [_rating, _setRating] = useState<FeedbackRating | undefined>(rating);

  const queryClient = useQueryClient();

  const { openModal, closeModal } = useModal({
    component:
      hasFeedback && feedback && feedback?.length > 0 ? (
        <ModalSubFeedbackExists
          onDismiss={() => {
            resetModal();
            closeModal();
          }}
          feedback={feedback}
        />
      ) : (
        <ModalSubFeedback
          onDismiss={() => {
            resetModal();
            closeModal();
          }}
          schoolName={school.name}
          requestId={requestId}
          isFavoritedSub={isFavoritedSub}
          initialRating={_rating}
          sub={{
            id: subId,
            name: subName,
            profileImgUrl: subProfileImgUrl,
            isSwingHero: subIsSwingHero,
          }}
        />
      ),
    modalOptions: { cssClass: "modal-sub-feedback" },
  });

  const autoOpenModalSubFeedback = modalId === requestId && isDisplayed;

  useEffect(() => {
    if (autoOpenModalSubFeedback) {
      openModal();
    }
  }, [autoOpenModalSubFeedback, openModal]);

  /********* Mutation ********/
  const { mutate: submitReview, isPending } = useMutation({
    mutationFn: (payload: SubmitFeedbackData) => {
      return submitSubFeedback(requestId, payload);
    },
    onSuccess: (updatedRequest) => {
      // Explicitly set to false to show the submitted state
      // and prevent the error state from being shown.
      setHasSubmissionError?.(false);

      if (initiationType === "START_WITH_RATING") {
        // Request Card - Past Request Page (list view of requests)
        // Using invalidateQueries here because even though the cache updates
        // correctly with setQueryData, the UI doesn't seem to re-render as expected.
        // TODO: Revisit this and see if we can switch to setQueryData.
        queryClient.invalidateQueries({ queryKey: ["requests", "past"] });
      } else {
        // SubInfoCard (single request view)
        queryClient.setQueryData(["requests", requestId], updatedRequest.data);
      }
    },
    // This will show error alert
    onError: () => setHasSubmissionError?.(true),
  });

  /********* Handlers ********/
  function handleSubmitReview() {
    if (!subLastFeedback?.rating) return;
    submitReview({
      rating: subLastFeedback.rating,
      preferred: false,
      isBlocked: false,
      reasons: ["REASON_OTHER"],
      otherReason: "repeat_review",
    });
  }

  /********* Render *********/
  // Optimistically show submitted state before query cache refresh kicks in
  if (hasFeedbackSubmissionError === false || hasFeedback || (feedback && feedback.length > 0)) {
    return (
      <div
        css={{
          cursor: "default",
          display: "flex",
          gap: 8,
          justifyContent: "center",
        }}
      >
        {!hideIcon && <Icon name="Check" color={Colors.green500} />}
        <p css={{ margin: 0, fontWeight: 600 }}>{translate("FEEDBACK_SUBMITTED_LABEL")}</p>
      </div>
    );
  }

  // If the sub has been previously reviewed by the same school,
  // we will show a prompt to the user to quickly submit a review
  // with the same rating.
  if (
    subLastFeedback &&
    // We only want to show the prompt if the previous review
    // was positive or neutral
    subLastFeedback.rating !== "RATING_NEGATIVE"
  ) {
    return (
      <SubRepeatReviewPrompt
        subName={subName}
        subLastFeedback={subLastFeedback}
        onQuickSubmit={handleSubmitReview}
        openModal={openModal}
        isSubmissionPending={isPending}
      />
    );
  }

  // Review Button shown on Past Request Card
  // TODO: Remove this condition once the feature flag is removed
  if (initiationType === "START_WITH_RATING") {
    return (
      <SubQuickReviewPrompt
        subName={subName}
        onRatingChange={(rating: FeedbackRating) => {
          _setRating(rating);
          openModal();
        }}
      />
    );
  }

  // Review Button shown on SubInfoCard
  return (
    <Button type="PRIMARY" label={translate("GIVE_FEEDBACK_LABEL")} onClick={openModal} isFluid />
  );
}
